// Phase RV-4a — SkipCycleBanner.
//
// Renders the most recent unread `skip_cycle` notification with the N12
// framing string. The framing string is read from `payload.framing` —
// never a hardcoded local copy that could drift from the DB trigger.
//
// Visual: Football Products (FpKit) Kalshi-clean. Inline styles, FpKit
// tokens, hairline borders, mono uppercase micro-labels.

import { useMemo, useState, type ReactNode } from "react";
import {
  useRolloverNotifications,
  type RolloverNotificationRow,
} from "../../rollover/useRolloverNotifications.ts";

export interface SkipCycleBannerProps {
  /** Override for tests so we can render without the hook. */
  notification?: RolloverNotificationRow;
  /** Override mark-read handler for tests. */
  onMarkRead?: (notification_id: string) => Promise<void> | void;
}

// --- FpKit inline primitives -------------------------------------------

function FpBadge({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "2px 7px",
        borderRadius: 4,
        background: "transparent",
        color: "var(--text-tertiary)",
        border: "1px solid var(--border-faint)",
      }}
    >
      {children}
    </span>
  );
}

function FpPill({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 4,
        border: "1px solid var(--border-faint)",
        background: "var(--bg-inset)",
        color: "var(--text-tertiary)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.12em",
        lineHeight: 1,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function FpEyebrow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--fp-accent-text)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span aria-hidden style={{ width: 16, height: 1, background: "currentColor", opacity: 0.7 }} />
      {children}
    </div>
  );
}

/**
 * Pick the latest unread skip_cycle notification (or null when none).
 * The notifications array is already sorted desc by `created_at` from
 * the hook.
 */
export function latestUnreadSkip(
  notifications: readonly RolloverNotificationRow[],
): RolloverNotificationRow | null {
  for (const n of notifications) {
    if (n.kind === "skip_cycle" && n.read_at === null) return n;
  }
  return null;
}

export function SkipCycleBanner({
  notification,
  onMarkRead,
}: SkipCycleBannerProps = {}): JSX.Element | null {
  const hook = useRolloverNotifications();
  const candidate = useMemo<RolloverNotificationRow | null>(() => {
    if (notification) return notification;
    return latestUnreadSkip(hook.notifications);
  }, [notification, hook.notifications]);
  const [dismissed, setDismissed] = useState(false);

  if (!candidate || dismissed) return null;

  const framingRaw = candidate.payload?.framing;
  const framing =
    typeof framingRaw === "string" && framingRaw.length > 0 ? framingRaw : "";
  if (!framing) return null;

  const tierRaw = candidate.payload?.tier;
  const tier = typeof tierRaw === "string" ? tierRaw.toUpperCase() : null;
  const windowRaw = candidate.payload?.window_label;
  const windowLabel =
    typeof windowRaw === "string" ? windowRaw.toUpperCase() : null;

  const handleMarkRead = async () => {
    if (onMarkRead) {
      await onMarkRead(candidate.notification_id);
    } else {
      await hook.markRead(candidate.notification_id);
    }
    setDismissed(true);
  };

  return (
    <section
      data-test="skip-cycle-banner"
      style={{
        padding: 14,
        border: "1px solid var(--border)",
        borderRadius: 10,
        marginTop: 14,
        marginBottom: 14,
        background: "var(--bg-elev)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <FpEyebrow>Skipped Cycle</FpEyebrow>
        <FpBadge>SKIPPED</FpBadge>
        {tier ? <FpPill>{tier}</FpPill> : null}
        {windowLabel ? <FpPill>{windowLabel}</FpPill> : null}
      </div>
      <div
        data-test="skip-cycle-framing"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--text-secondary)",
          marginTop: 10,
          lineHeight: 1.55,
        }}
      >
        {framing}
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          data-test="skip-cycle-mark-read"
          onClick={() => {
            void handleMarkRead();
          }}
          style={{
            background: "transparent",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "6px 12px",
            color: "var(--text-secondary)",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </section>
  );
}
